import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Alert from "../../../Common/Alert";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import Swal from "sweetalert2";
import IsLogin from "../../../Common/IsLogin";
import ReportDateRangeCsv from "../../../Common/ReportDateRangeCSV"; // <-- adjust path
import { ProgressSpinner } from "primereact/progressspinner";

const DailyProductionReport = () => {
  const location = useLocation();
  const [receiveData, setReceiveData] = useState([]);
  const [alert, setAlert] = useState(null);
  const date = new Date();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const [inputs, setInputs] = useState({
    date_from: getCurrentDate(),
    date_to: getCurrentDate(),
  });
  const [loading, setLoading] = useState(false);

  function getCurrentDate() {
    const dateObj = new Date();
    const month = ("0" + (dateObj.getMonth() + 1)).slice(-2);
    const day = ("0" + dateObj.getDate()).slice(-2);
    const year = dateObj.getFullYear();
    return `${year}-${month}-${day}`;
  }

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const fetchData = (dateFromParam, dateToParam) => {
    const dateFrom = dateFromParam || inputs.date_from || getCurrentDate();
    const dateTo = dateToParam || inputs.date_to || getCurrentDate();
    setLoading(true);
    Http.get(
      `/production-and-transfer-list?date_from=${dateFrom}&date_to=${dateTo}`,
    )
      .then((res) => {
        setReceiveData(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    DatatableFunc();
    fetchData();
  }, []);

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const formSubmit = () => {
    fetchData(inputs.date_from, inputs.date_to);
  };

  const calculateRowValues = (item) => {
    const receivedFromJigger = (item.receive_data || []).reduce(
      (sum, r) => sum + Number(r.receive_qty || 0),
      0,
    );
    const finishingLoss = (item.loss_data || []).reduce(
      (sum, l) =>
        sum +
        Number(l.before_production_loss_qty || 0) +
        Number(l.after_production_loss_qty || 0),
      0,
    );
    const transferredBk = (item.transfer_data || []).reduce(
      (sum, t) => sum + Number(t.delivery_qty || 0),
      0,
    );

    const openingUnfinished = 0;
    const openingFinished = 0;
    const total10_11 = openingUnfinished + openingFinished + receivedFromJigger;

    const closingUnfinished = 0;
    const closingFinished = receivedFromJigger - finishingLoss - transferredBk;
    const total9_9 = finishingLoss + transferredBk;

    return {
      openingUnfinished,
      openingFinished,
      receivedFromJigger,
      total10_11,
      finishingLoss,
      transferredBk,
      total9_9,
      closingUnfinished,
      closingFinished,
    };
  };

  const totals = receiveData.reduce(
    (acc, item) => {
      const {
        openingUnfinished,
        openingFinished,
        receivedFromJigger,
        total10_11,
        finishingLoss,
        transferredBk,
        total9_9,
        closingUnfinished,
        closingFinished,
      } = calculateRowValues(item);

      return {
        totalOpeningUnfinished: acc.totalOpeningUnfinished + openingUnfinished,
        totalOpeningFinished: acc.totalOpeningFinished + openingFinished,
        totalReceivedFromJigger:
          acc.totalReceivedFromJigger + receivedFromJigger,
        total10_11: acc.total10_11 + total10_11,
        totalFinishingLoss: acc.totalFinishingLoss + finishingLoss,
        totalTransferredBk: acc.totalTransferredBk + transferredBk,
        total9_9: acc.total9_9 + total9_9,
        totalClosingUnfinished: acc.totalClosingUnfinished + closingUnfinished,
        totalClosingFinished: acc.totalClosingFinished + closingFinished,
      };
    },
    {
      totalOpeningUnfinished: 0,
      totalOpeningFinished: 0,
      totalReceivedFromJigger: 0,
      total10_11: 0,
      totalFinishingLoss: 0,
      totalTransferredBk: 0,
      total9_9: 0,
      totalClosingUnfinished: 0,
      totalClosingFinished: 0,
    },
  );

  const getCsvData = () => {
    const headers = [
      "S/N",
      "Name of Products",
      "Code No.",
      "Shape",
      "Opening Balance - Unfinished",
      "Opening Balance - Finished",
      "Received from Jigger Section",
      "10 + 11 Total",
      "Finishing Loss",
      "Transferred B.K Section",
      "9 + 9 Total",
      "Closing Balance - Unfinished",
      "Closing Balance - Finished",
    ];

    const rows = receiveData.map((item, index) => {
      const {
        openingUnfinished,
        openingFinished,
        receivedFromJigger,
        total10_11,
        finishingLoss,
        transferredBk,
        total9_9,
        closingUnfinished,
        closingFinished,
      } = calculateRowValues(item);
      return [
        index + 1,
        item.product_name,
        item.product_code,
        item.shape,
        openingUnfinished,
        openingFinished,
        receivedFromJigger,
        total10_11,
        finishingLoss,
        transferredBk,
        total9_9,
        closingUnfinished,
        closingFinished,
      ];
    });

    const footerRows = [
      [
        "",
        "Total",
        "",
        "",
        totals.totalOpeningUnfinished,
        totals.totalOpeningFinished,
        totals.totalReceivedFromJigger,
        totals.total10_11,
        totals.totalFinishingLoss,
        totals.totalTransferredBk,
        totals.total9_9,
        totals.totalClosingUnfinished,
        totals.totalClosingFinished,
      ],
    ];

    return { headers, rows, footerRows };
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div>
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <h5 className="am-title">Daily Production Report</h5>
          </div>
          <div className="am-pagebody">
            <Alert alert={alert} />
            <div className="card pd-20 pd-sm-40">
              <ReportDateRangeCsv
                dateFrom={inputs.date_from}
                dateTo={inputs.date_to}
                onChange={handleChange}
                onSearch={formSubmit}
                csvFilename={`daily-production-report-finishing_${inputs.date_from}_to_${inputs.date_to}.csv`}
                getCsvData={getCsvData}
              />
            </div>
          </div>
          <div className="am-pagebody">
            <div className="card pd-20 pd-sm-40">
              {/* <h6 className="card-body-title mg-sm-b-30">Datatable</h6> */}
              <div className="row">
                <div className="col-12">
                  <div className="text-center">
                    <h5 className="text-uppercase text-sm font-weight-bolder">
                      Artisan Ceramics Limited
                    </h5>
                    <p>
                      House-16, Road-07, Sector-01, Uttara, Dhaka
                      <br />
                      <b>Daily Production Report</b>
                      <br />
                      (Finishing)
                    </p>
                  </div>
                  <div
                    className="table-responsive p-0 pb-2"
                    style={{ maxHeight: "70vh", overflowY: "auto" }}
                  >
                    {" "}
                    <table className="table table-sm table-bordered table-striped align-items-center justify-content-center mb-0 _datatable">
                      <thead
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                          backgroundColor: "#fff",
                        }}
                      >
                        <tr>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            S/N
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            Name of Products
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Code No.
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Shape
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            colSpan={2}
                          >
                            Opening Balance
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Received from Jigger Section
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            10 + 11 <br /> Total
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Finishing Loss
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Transferred B.K Section
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            9 + 9 <br /> Total
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            colSpan={2}
                          >
                            Closing Balance
                          </th>
                        </tr>
                        <tr>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2"></th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2"></th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"></th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"></th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Unfinished
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Finished
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"></th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"></th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"></th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"></th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"></th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Unfinished
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Finished
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {loading ? (
                          <tr>
                            <td
                              className="text-center"
                              colSpan={13}
                              style={{ padding: "2rem" }}
                            >
                              <ProgressSpinner
                                style={{ width: "40px", height: "40px" }}
                                strokeWidth="4"
                              />
                            </td>
                          </tr>
                        ) : receiveData && receiveData.length > 0 ? (
                          receiveData.map((item, index) => {
                            const {
                              openingUnfinished,
                              openingFinished,
                              receivedFromJigger,
                              total10_11,
                              finishingLoss,
                              transferredBk,
                              total9_9,
                              closingUnfinished,
                              closingFinished,
                            } = calculateRowValues(item);

                            return (
                              <tr key={index}>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {index + 1}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.product_name}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.product_code}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.shape}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {openingUnfinished}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {openingFinished}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {receivedFromJigger}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {total10_11}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {finishingLoss}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {transferredBk}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {total9_9}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {closingUnfinished}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {closingFinished}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                              colSpan={13}
                            >
                              No data found
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2"
                            colSpan={4}
                          >
                            Total
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalOpeningUnfinished}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalOpeningFinished}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalReceivedFromJigger}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.total10_11}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalFinishingLoss}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalTransferredBk}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.total9_9}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalClosingUnfinished}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalClosingFinished}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyProductionReport;
